import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Zap, Download, Loader2, Wifi, WifiOff, Check, User, FileArchive } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Toaster } from '../components/ui/toaster';
import NavigationMenu from '../components/NavigationMenu';
import DynamicFooter from '../components/DynamicFooter';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_URL = process.env.REACT_APP_BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

const P2PReceive = () => {
  const { roomId } = useParams();
  const [roomInfo, setRoomInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connected, setConnected] = useState(false);
  const [receiving, setReceiving] = useState(false);
  const [progress, setProgress] = useState(0);
  const [complete, setComplete] = useState(false);
  const [receivedFiles, setReceivedFiles] = useState([]);

  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const fileBufferRef = useRef([]);
  const currentFileRef = useRef(null);
  const receivedFilesRef = useRef([]);
  const completeRef = useRef(false);
  const iceCandidateQueueRef = useRef([]);

  useEffect(() => {
    loadRoomInfo();
    return () => {
      wsRef.current?.close();
      pcRef.current?.close();
    };
  }, [roomId]);

  const loadRoomInfo = async () => {
    try {
      const res = await axios.get(`${API}/p2p/room/${roomId}`);
      setRoomInfo(res.data);
      connectWebSocket();
    } catch (err) {
      setError(err.response?.data?.detail || 'Salle P2P non trouvee ou expiree');
    }
    setLoading(false);
  };

  const connectWebSocket = useCallback(() => {
    const ws = new WebSocket(`${WS_URL}/api/ws/p2p/${roomId}/receiver`);
    wsRef.current = ws;

    ws.onmessage = async (event) => {
      const msg = JSON.parse(event.data);

      if (msg.type === 'peer-connected') {
        setConnected(true);
      }

      if (msg.type === 'offer') {
        await handleOffer(msg.sdp, ws);
      }

      if (msg.type === 'ice-candidate' && msg.candidate) {
        // Queue ICE candidates if PeerConnection not ready yet
        if (pcRef.current && pcRef.current.remoteDescription) {
          try {
            await pcRef.current.addIceCandidate(new RTCIceCandidate(msg.candidate));
          } catch (e) {
            console.warn('ICE candidate error:', e);
          }
        } else {
          iceCandidateQueueRef.current.push(msg.candidate);
        }
      }

      if (msg.type === 'transfer-progress') {
        setProgress(msg.progress);
        if (!receiving) setReceiving(true);
      }

      if (msg.type === 'peer-disconnected') {
        if (!completeRef.current) setConnected(false);
      }
    };

    ws.onclose = () => {
      if (!completeRef.current) setConnected(false);
    };
  }, [roomId]);

  const processIceCandidateQueue = async () => {
    const pc = pcRef.current;
    if (!pc) return;
    while (iceCandidateQueueRef.current.length > 0) {
      const candidate = iceCandidateQueueRef.current.shift();
      try {
        await pc.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (e) {
        console.warn('Queued ICE candidate error:', e);
      }
    }
  };

  const handleOffer = async (sdp, ws) => {
    const pc = new RTCPeerConnection({
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
        { urls: 'stun:stun2.l.google.com:19302' }
      ]
    });
    pcRef.current = pc;

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        ws.send(JSON.stringify({ type: 'ice-candidate', candidate: event.candidate }));
      }
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'connected') {
        setConnected(true);
      }
      if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
        if (!completeRef.current) {
          setError('La connexion P2P a ete interrompue.');
        }
      }
    };

    pc.ondatachannel = (event) => {
      const dc = event.channel;
      dc.binaryType = 'arraybuffer';
      setReceiving(true);

      dc.onmessage = (e) => {
        if (typeof e.data === 'string') {
          let msg;
          try {
            msg = JSON.parse(e.data);
          } catch {
            return;
          }

          if (msg.type === 'file-meta') {
            currentFileRef.current = { name: msg.name, size: msg.size, mimeType: msg.mimeType, index: msg.index };
            fileBufferRef.current = [];
          }

          if (msg.type === 'file-end') {
            const blob = new Blob(fileBufferRef.current, { type: currentFileRef.current?.mimeType || 'application/octet-stream' });
            const url = URL.createObjectURL(blob);
            const fileInfo = { ...currentFileRef.current, url, blob };
            receivedFilesRef.current.push(fileInfo);
            setReceivedFiles([...receivedFilesRef.current]);
            fileBufferRef.current = [];
          }

          if (msg.type === 'transfer-done') {
            completeRef.current = true;
            setComplete(true);
            setReceiving(false);
            setProgress(100);
          }
        } else {
          // Binary data chunk (ArrayBuffer)
          fileBufferRef.current.push(new Uint8Array(e.data));
        }
      };

      dc.onerror = (err) => {
        console.error('DataChannel error:', err);
      };
    };

    await pc.setRemoteDescription(new RTCSessionDescription(sdp));
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    ws.send(JSON.stringify({ type: 'answer', sdp: pc.localDescription }));

    // Process any ICE candidates that arrived before PeerConnection was ready
    await processIceCandidateQueue();
  };

  const downloadFile = (file) => {
    const a = document.createElement('a');
    a.href = file.url;
    a.download = file.name;
    a.click();
  };

  const downloadAll = () => {
    receivedFiles.forEach(f => downloadFile(f));
  };

  const formatSize = (bytes) => {
    if (!bytes) return '0 o';
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} Ko`;
    if (bytes < 1073741824) return `${(bytes / 1048576).toFixed(1)} Mo`;
    return `${(bytes / 1073741824).toFixed(2)} Go`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-950 via-slate-900 to-blue-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-950 via-slate-900 to-blue-900 flex flex-col">
        <NavigationMenu />
        <main className="flex-1 flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            <WifiOff className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Salle non trouvee</h1>
            <p className="text-blue-300/60">{error}</p>
          </div>
        </main>
        <DynamicFooter fixed={true} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 via-slate-900 to-blue-900 text-white relative overflow-hidden">
      <NavigationMenu />
      <Toaster />

      {/* Background elements matching Home */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
      </div>

      <main className="flex-1 pt-28 pb-20 px-4 relative z-10">
        <div className="max-w-xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-blue-500/20 border border-blue-500/30 rounded-full text-blue-300 text-sm mb-4">
              <Zap className="w-4 h-4" /> Transfert P2P
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Reception en direct</h1>
            <p className="text-blue-200/60 text-sm">Transfert direct navigateur-a-navigateur</p>
          </div>

          <div className="bg-blue-950/50 backdrop-blur-xl border border-blue-500/20 rounded-2xl p-6 space-y-6 shadow-2xl shadow-blue-500/10">

            {/* Sender info */}
            {roomInfo && (
              <div className="flex items-center gap-4 pb-4 border-b border-blue-500/15">
                <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <p className="text-white font-medium">{roomInfo.sender_name}</p>
                  <p className="text-sm text-blue-300/50">{roomInfo.filename} - {formatSize(roomInfo.file_size)}</p>
                </div>
              </div>
            )}

            {/* Connection status */}
            {!complete && !receiving && (
              <div className="text-center space-y-4 py-4">
                {connected ? (
                  <div className="flex items-center justify-center gap-2 text-emerald-400">
                    <Wifi className="w-5 h-5" />
                    <span className="font-medium">Connecte a l'expediteur</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2 text-orange-400">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="font-medium">Connexion a l'expediteur...</span>
                  </div>
                )}
              </div>
            )}

            {/* Transfer progress */}
            {receiving && !complete && (
              <div className="space-y-3 py-2">
                <div className="flex items-center justify-center gap-2 text-cyan-400 mb-2">
                  <Zap className="w-5 h-5 animate-pulse" />
                  <span className="font-medium text-sm">Reception en cours...</span>
                </div>
                <div className="relative w-full bg-slate-800 rounded-full h-3 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-cyan-500 transition-all duration-300 rounded-full" style={{ width: `${progress}%` }} />
                </div>
                <p className="text-center text-white font-bold text-xl">{progress}%</p>
                <p className="text-center text-xs text-blue-300/40">Gardez cette page ouverte</p>
              </div>
            )}

            {/* Transfer complete */}
            {complete && (
              <div className="space-y-4">
                <div className="text-center">
                  <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center mx-auto shadow-lg shadow-emerald-500/30 mb-4">
                    <Check className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-1">Transfert termine !</h2>
                  <p className="text-sm text-blue-300/50">{receivedFiles.length} fichier(s) recu(s)</p>
                </div>

                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {receivedFiles.map((f, i) => (
                    <div key={i} className="flex items-center gap-3 bg-slate-900/50 rounded-lg p-3">
                      <FileArchive className="w-5 h-5 text-cyan-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm truncate">{f.name}</p>
                        <p className="text-xs text-blue-300/50">{formatSize(f.size)}</p>
                      </div>
                      <Button onClick={() => downloadFile(f)} size="sm" className="bg-blue-500 hover:bg-blue-600 text-white shrink-0" data-testid={`download-file-${i}`}>
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>

                {receivedFiles.length > 1 && (
                  <Button onClick={downloadAll} className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold" data-testid="download-all-p2p">
                    <Download className="w-4 h-4 mr-2" /> Tout telecharger
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
      <DynamicFooter fixed={true} />
    </div>
  );
};

export default P2PReceive;
